/**
 * A pet coat's own effects (skins v2, 2026-10-21: no coat is just a recolour). The coat's GLB carries its geometry
 * (crowns, ruffs, crystals, wisps …: art/unitkit/pet_coats.py) and a `flourish` clip (art/unitkit/pet_flourish.py);
 * this is the data the client plays on top (views/petCoatFx.ts): an ambient sprite burst off one of its rig parts every
 * few seconds, and the flourish — its clip played in place of some of the fidgets, with a burst at its peak.
 * Pure data (no three.js), so the budgets are unit-tested (test/petCoatFx.test.ts).
 */

/**
 * The coat particle kinds: `sparkles` (rising stars), `glints` (stars in place), `motes` (slow drifting lights),
 * `embers` (rising, cooling glows), `snow` (flakes drifting down round it), `wisps` (dark smoke curling up with a
 * glowing mote), `leaves` / `petals` (sprites drifting down and turning), `bubbles` (rising and popping).
 */
export type PetFxKind = "sparkles" | "glints" | "motes" | "embers" | "snow" | "wisps" | "leaves" | "petals" | "bubbles";
export const PET_FX_KINDS: readonly PetFxKind[] = ["sparkles", "glints", "motes", "embers", "snow", "wisps", "leaves", "petals", "bubbles"];

/** One burst: its kind, colour (sRGB hex) and particle count. */
export interface PetFxBurst {
  readonly kind: PetFxKind;
  readonly colour: number;
  readonly count: number;
}

/** The coat's ambient effect: a burst (plus an optional second one) every `every` s off rig part `node`. */
export interface PetAmbient extends PetFxBurst {
  /** Seconds between bursts, min and max. */
  readonly every: readonly [number, number];
  /** The rig part they come off (`body`, `head`, `flag0` …: the model's `<model>.<node>`); missing, the body. */
  readonly node: string;
  /** Spread radius and lift over the part, in pet heights (defaults 0.35 and 0.3). */
  readonly radius?: number;
  readonly lift?: number;
  /** A second burst at the same moment (counts toward the budget). */
  readonly also?: PetFxBurst;
}

/** The coat's flourish: its clip, the share of fidgets it replaces, and the burst at its peak (`peak` s in). */
export interface PetFlourish {
  readonly clip: string;
  readonly share: number;
  readonly peak: number;
  readonly burst: PetFxBurst;
  /** Where the burst comes from (default: the ambient's node). */
  readonly node?: string;
}

/** A coat's effects. */
export interface PetCoatFxSpec {
  readonly ambient: PetAmbient;
  readonly flourish: PetFlourish;
}

/** Budgets (a pet is one small body; these keep a coat below a tower kit's idle and flourish). */
export const PET_FX_BUDGET = {
  /** Particles in one ambient burst (with `also`). */
  AmbientParticles: 6,
  /** Particles in the flourish's burst. */
  FlourishParticles: 10,
  /** The shortest gap between ambient bursts (s). */
  MinEvery: 0.8,
} as const;
